import { useEffect, useRef, useState } from 'react'
import * as React from 'react'
import cx from 'classnames'
import { isEmpty } from 'lodash'
import { REACT_APP_FRONTEND_URL } from 'env'
import { WhiteButton } from 'components/Buttons/WhiteButton'
import HandbookIcon from 'components/Handbook2_0/HandbookIcon'
import Styles from '../PublishHandbook.module.scss'
import { SettingsFormProps } from './Types'
import { DomainCallout } from '../DomainCallout/DomainCallout'
import preview from './DocStep2.png'
import { useHandbook } from 'components/Handbook2_0/hooks'
import { useUpdateHandbook } from 'components/Handbook2_0/HandbookSettings/useUpdateHandbook'
import { reportError } from 'utils/error'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { handbookLink } from 'components/Handbook2_0/util'
import { Button } from 'components/Button'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useIsBlaze } from 'hooks/useIsBlaze'

export const SettingsForm = observer(({ setStep, submitWizard, originalPath, workspaceDomain }: SettingsFormProps) => {
  const handbookV2 = useHandbook()
  const { name, image_url } = handbookV2 || {}
  const domainName =
    workspaceDomain &&
    (workspaceDomain.subdomain
      ? `${workspaceDomain.name}.${REACT_APP_FRONTEND_URL.split('//')[1]}`
      : workspaceDomain.name)
  const link = handbookV2 ? handbookLink(handbookV2) : ''
  const defaultUrl = !!workspaceDomain ? `${domainName}${originalPath}` : `${REACT_APP_FRONTEND_URL}${originalPath}`
  const handbookPath = link && link.substring(1)
  const [newPath, setNewPath] = useState(originalPath)
  const [initialLoad, setInitialLoad] = useState(true)
  const [newPathError, setNewPathError] = useState<string | null>(null)
  const pathRef = useRef<HTMLInputElement>(null)
  const isBlaze = useIsBlaze()
  const previewUrl =
    !!workspaceDomain &&
    (workspaceDomain.subdomain
      ? `${workspaceDomain.name}.${REACT_APP_FRONTEND_URL.split('//')[1]}/${newPath}`
      : `${workspaceDomain.name}/${newPath}`)
  const store = useRootStore().handbooks
  const [updateHandbook] = useUpdateHandbook(handbookV2, store)

  const invalidPathMessage = (path: string | undefined) => {
    if (!path || isEmpty(path)) return 'Cannot be empty'
    if (path?.includes(' ')) return 'Cannot contain whitespace'
    return null
  }

  const handlePathInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const errorMsg = invalidPathMessage(value)

    setNewPathError(errorMsg)
    setNewPath(value)
  }

  useEffect(() => {
    if (pathRef.current) pathRef.current.focus()
  }, [pathRef])

  useEffect(() => {
    const submitPath = async () => {
      if (initialLoad) {
        setInitialLoad(false)
        return
      }
      if (!workspaceDomain) return
      if (newPathError) return

      const modifiedPath = handbookPath === newPath ? null : newPath
      let errors, res

      try {
        errors = await updateHandbook({ custom_path: modifiedPath })
        if (!errors) return

        // expect the first error to be a custom path validation error
        const errorMessage = errors[0][1] === 'Invalid path' ? 'Invalid path' : 'That path has already been taken.'
        setNewPathError(errorMessage)
        return
      } catch (err) {
        errors = err
      }

      reportError(new Error('Failed to update handbook image'), {
        severity: 'error',
        metaData: (errors as object) || res,
      })
      showToastMessage('Something went wrong', 'error')
    }

    const delayDebounceFn = setTimeout(() => {
      submitPath()
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [newPath]) // eslint-disable-line react-hooks/exhaustive-deps

  const entityType = isBlaze ? 'project' : 'folder'

  const subtitle = () => {
    if (!workspaceDomain)
      return `Your workspace uses the Blaze default URL. This ${entityType} can be accessed only through this URL:`
    if (workspaceDomain.subdomain)
      return 'This is the subdomain set up in your workspace settings. You can customize the URL path.'
    return 'This is the custom domain set up in your workspace settings. You can customize the URL path.'
  }

  const clearInput = () => {
    if (newPath !== originalPath) return

    setNewPath('')
  }

  return (
    <div className={cx(Styles.frame, Styles.handbookSetting, Styles.fadeIn)}>
      <div className={Styles.leftColumn}>
        <div className={Styles.frameHeader}>Set up URL for your {entityType}</div>
        <div className={Styles.form}>
          <div className={Styles.subtitle}>{subtitle()}</div>
          <div className={Styles.formField}>
            <div>
              {!!workspaceDomain ? (
                <>
                  <div className={cx(Styles.urlInput, { [Styles.error]: !!newPathError })} tabIndex={0}>
                    <div>
                      {workspaceDomain.subdomain
                        ? `${workspaceDomain.name}.${REACT_APP_FRONTEND_URL.split('//')[1]}/`
                        : `${workspaceDomain.name}/`}
                    </div>
                    <input onChange={handlePathInputChange} value={newPath} ref={pathRef} onClick={clearInput} />
                  </div>
                  {newPathError && <div className={Styles.errorText}>{newPathError}</div>}
                  <div
                    className={Styles.resetButton}
                    onClick={() => {
                      setNewPath(originalPath)
                      setNewPathError(null)
                    }}
                  >
                    Reset URL
                  </div>
                </>
              ) : (
                <div className={Styles.urlInputStatic}>{defaultUrl}</div>
              )}
            </div>
          </div>
        </div>

        <div className={Styles.domainCalloutContainer}>
          <DomainCallout workspaceDomain={workspaceDomain} entityType={entityType} />
        </div>
        {setStep && (
          <div className={Styles.frameNavButtons}>
            <WhiteButton className={Styles.whiteButton} onClick={() => setStep?.(2)}>
              Back
            </WhiteButton>
            <Button
              className={Styles.frameNavButton}
              onPress={submitWizard}
              isDisabled={!!newPathError || !!invalidPathMessage(newPath)}
              size="xl"
            >
              Publish Handbook
            </Button>
          </div>
        )}
      </div>
      <div className={Styles.rightColumn}>
        <div className={cx(Styles.previewUrl, Styles.active)}>{previewUrl ? previewUrl : defaultUrl}</div>
        <img className={Styles.image} src={preview} alt="Preview your handbook logo" />
        <div className={cx(Styles.previewLogo, image_url ? Styles.customLogo : Styles.defaultLogo)}>
          {image_url ? (
            <img src={image_url} alt="Logo preview" className={Styles.customLogo} />
          ) : (
            <>
              {handbookV2?.emoji ? <span>{handbookV2.emoji}</span> : <HandbookIcon folder={!!handbookV2?.folder} />}
              <div className={Styles.previewText}>{name}</div>
            </>
          )}
        </div>
        <div className={Styles.previewNavTitle}>
          {handbookV2?.emoji ? <span>{handbookV2.emoji}</span> : <HandbookIcon folder={!!handbookV2?.folder} />}
          <div className={Styles.previewText}>{name}</div>
        </div>
        {image_url && (
          <div className={Styles.previewSidebar}>
            {handbookV2?.emoji ? <span>{handbookV2.emoji}</span> : <HandbookIcon folder={!!handbookV2?.folder} />}
            <div className={Styles.previewText}>{name}</div>
          </div>
        )}
        <div className={Styles.previewTitle}>
          {handbookV2?.emoji ? <span>{handbookV2.emoji}</span> : <HandbookIcon folder={!!handbookV2?.folder} />}
          <div className={Styles.previewText}>{name}</div>
        </div>
      </div>
    </div>
  )
})
